import { MediaMatcher } from '@angular/cdk/layout';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ReviewReasonComponent } from 'src/app/admin/components/review-reason/review-reason.component';
import { AppComponent } from 'src/app/app.component';
import { ApiService } from 'src/app/services/api.service';
import { NotifyService } from 'src/app/services/notify.service';
import { ValueService } from 'src/app/services/value.service';
import { environment } from 'src/environments/environment';
declare let require: any;
const FileSaver = require('file-saver');

@Component({
  selector: 'app-admin-hr',
  templateUrl: './admin-hr.component.html',
  styleUrls: ['./admin-hr.component.css'],
})
/**
 * The AdminHrComponent is responsible for managing HR administration tasks within the admin panel.
 * It handles fetching and displaying HR data, approving or rejecting items, downloading files,
 * and interacting with guidelines and review dialogs. The component is designed to be responsive
 * and utilizes various services for API communication, notifications, and UI dialogs.
 *
 * @remarks
 * This component uses reactive forms for status updates and interacts with session storage
 * to retrieve incubator-related information. It also manages loading indicators and
 * provides utility methods for status display and file downloads.
 *
 * @example
 * // Usage in a template
 * <app-admin-hr></app-admin-hr>
 */
export class AdminHrComponent implements OnInit {
  updateForm: FormGroup;
  private getApi = environment.url + environment.hrGet;
  private updateStatusApi = environment.url + environment.updateStatus;
  /**
   * Initializes the AdminHrComponent with required services and sets up the update form and mobile query listener.
   *
   * @param changeDetectorRef Reference to Angular's change detector for triggering change detection.
   * @param media Service to match media queries for responsive design.
   * @param getdata Service to retrieve values or data needed by the component.
   * @param fb FormBuilder service for creating reactive forms.
   * @param makeapi Service for making API calls.
   * @param dialog Material dialog service for opening dialogs.
   * @param notify Service for displaying notifications.
   * @param appCom Reference to the root AppComponent.
   * @param http Angular HttpClient for HTTP requests.
   */
  constructor(
    changeDetectorRef: ChangeDetectorRef,
    media: MediaMatcher,
    private getdata: ValueService,
    private fb: FormBuilder,
    private makeapi: ApiService,
    public dialog: MatDialog,
    private notify: NotifyService,
    private appCom: AppComponent,
    private http: HttpClient
  ) {
    this.updateForm = this.fb.group({
      incubatorId: [''],
    });
    this.mobileQuery = media.matchMedia('(max-width: 992px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
  }
  // mobileQuery
  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;
  ngAfterViewInit(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
  }

  incubaterName;
  ngOnInit(): void {
    this.incubaterName = sessionStorage.getItem('adminIncubName');
    let getform = this.updateForm.value;
    getform.incubatorId = sessionStorage.getItem('incubator');
    this.updateForm.patchValue(getform);
    this.getDataApi();
    this.guidelineDatas();
  }

  getValue: any = {};
  /**
   * Fetches data from the API using the incubator ID from session storage,
   * shows a loading indicator during the request, and updates the component's data on success.
   */
  getDataApi() {
    this.getdata.amizhthLoader('show');
    this.makeapi
      .method(this.getApi + sessionStorage.getItem('incubator'), '', 'get')
      .subscribe((data) => {
        this.getdata.amizhthLoader('hide');
        this.getValue = data;
      });
  }
  /**
   * Returns the color associated with the given value's question status.
   * @param val - The value to determine the question status color for.
   * @returns The color corresponding to the question status.
   */
  pickColor(val) {
    return this.getdata.questionStatus(val);
  }
  /**
   * Returns the width associated with the given status value.
   * @param val - The status value to determine the width for.
   * @returns The width corresponding to the provided status value.
   */
  pickWidth(val) {
    return this.getdata.statusWidth(val);
  }
  /**
   * Returns the status name for the given value using the getdata service.
   * @param val - The value to retrieve the status name for.
   * @returns The status name corresponding to the provided value.
   */
  innerValue(val) {
    return this.getdata.StatusName(val);
  }
  /**
   * Downloads a PDF file from the specified URL using FileSaver.
   * @param value - The URL of the PDF file to download.
   */
  download(value) {
    const pdfUrl = value;
    const pdfName = 'your_pdf_file';
    FileSaver.saveAs(pdfUrl, pdfName);
  }

  /**
   * Approves an item by adding a control to the form, submitting the update, and handling the response.
   * @param keys - The key to add as a control in the form.
   * @param id - The value to set for the added control.
   */
  approveBtn(keys, id) {
    const fc = new FormControl('');
    this.updateForm.addControl(keys, fc);
    this.updateForm.controls[keys].setValue(id);
    this.getdata.amizhthLoader('show');
    let getform = this.updateForm.value;
    this.makeapi
      .method(this.updateStatusApi, getform, 'post')
      .subscribe((data) => {
        this.getdata.amizhthLoader('hide');
        if (data.responseStatus == 'Success') {
          this.notify.notifysuccess(data.responseMessage);
          this.updateForm.removeControl(keys);
          this.getDataApi();
        } else {
          this.notify.notifyError(data.responseMessage);
        }
      });
  }
  /**
   * Handles the rejection action by adding a control to the form,
   * updating its value, sending a status update request, and
   * displaying notifications based on the response.
   *
   * @param keys - The key to add as a control to the form.
   * @param id - The value to set for the newly added form control.
   */
  rejectBtn(keys, id) {
    const fc = new FormControl('');
    this.updateForm.addControl(keys, fc);
    this.updateForm.controls[keys].setValue(id);
    this.getdata.amizhthLoader('show');
    let getform = this.updateForm.value;
    this.makeapi
      .method(this.updateStatusApi, getform, 'post')
      .subscribe((data) => {
        this.getdata.amizhthLoader('hide');
        if (data.responseStatus == 'Success') {
          this.notify.notifysuccess(data.responseMessage);
          this.updateForm.removeControl(keys);
          this.getDataApi();
        } else {
          this.notify.notifyError(data.responseMessage);
        }
      });
  }
  /**
   * Opens the ReviewReasonComponent dialog to review an item with the given key and status.
   * Refreshes the data after the dialog is closed.
   *
   * @param key - The unique identifier for the item to review.
   * @param status - The current status of the item.
   */
  reviewBtn(key, status) {
    const dialogRef = this.dialog.open(ReviewReasonComponent, {
      width: '390px',
      data: { _key: key, _status: status },
    });
    dialogRef.afterClosed().subscribe((result) => {
      this.getDataApi();
    });
  }
  guidelinesBtn(data) {
    this.appCom.guidelinesModal(data);
  }
  guidelineValue: any = [];
  guidelineDatas() {
    this.http.get('/assets/json/guideline.json').subscribe((data) => {
      this.guidelineValue = data[3].questions;
    });
  }
  step = 0;
  setStep(index: number) {
    this.step = index;
  }
  nextStep() {
    this.step++;
  }
  prevStep() {
    this.step--;
  }
}
